import './styles.css';
import { api, ApiError, canReadAudit, getSession, type OrganisationChoice, type SignInResponse } from './api';
import { esc, html, loading, toast } from './ui';
import { bindSearch } from './search';
import {
  bindChooseOrg,
  bindCreateOrg,
  bindForgot,
  bindLogin,
  bindRegister,
  bindReset,
  bindVerify,
  chooseOrgView,
  createOrgView,
  forgotView,
  loginView,
  registerView,
  resetView,
  verifyView,
} from './views/auth';
import { renderDashboard, renderFleet } from './views/dashboard';
import { renderAircraft, TABS, type TabKey } from './views/aircraft';
import { renderPlanning } from './views/planning';
import { renderWorkPack } from './views/workpack';
import { renderOrganisation } from './views/organisation';
import { renderConsole } from './views/console';
import { renderAudit } from './views/audit';
import { renderManual } from './views/manual';
import { renderTypes } from './views/types';
import { openEstimate } from './dialogs/estimate';
import { renderDefects } from './views/defects';
import { renderReliability } from './views/reliability';
import { renderSafety } from './views/safety';
import { renderTraining } from './views/training';
import { renderBilling } from './views/billing';
import { renderOwnerPortal } from './views/owner';
import { renderCapacity } from './views/capacity';
import { exportFleetStatus } from './dialogs/fleet-status';
import { renderTechlog } from './views/techlog';
import { renderStores } from './views/stores';
import { renderStockImport } from './views/stock-import';
import { renderStockCheck } from './views/stock-check';
import { renderAmpExtraction } from './views/amp-extraction';
import { addAircraft } from './dialogs/add-aircraft';
import { newWorkPack } from './dialogs/work-pack';
import { editProfile } from './dialogs/profile';
import { assistantAvailable, closeAssistant, setContext, toggleAssistant } from './assistant';
import { watchDateInputs } from './date-input';

const app = document.querySelector<HTMLElement>('#app')!;

// Every date box, on every screen, written the organisation's way. Once.
watchDateInputs();

/** Screens reachable without a session. */
const PUBLIC_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/demo'];

const NAV = [
  { group: 'Airworthiness', items: [
    { path: '/dashboard', icon: '◈', label: 'Dashboard' },
    { path: '/fleet', icon: '✈', label: 'Fleet' },
    // Beside the fleet, because it is the record behind it — the designator
    // the directive check matches on and the mass the useful load comes
    // from. Not under Organisation: it is about aeroplanes, not about us.
    { path: '/types', icon: '◇', label: 'Aircraft types' },
    { path: '/planning', icon: '▤', label: 'Planning & work packs' },
    { path: '/techlog', icon: '✎', label: 'Tech log' },
    { path: '/defects', icon: '⚑', label: 'Defects & MEL' },
    { path: '/stores', icon: '▦', label: 'Stores', needs: 'stores' },
  ] },
  { group: 'Analysis', items: [{ path: '/reliability', icon: '◔', label: 'Reliability & trends' }] },
  // Whoever runs the installation, not whoever uses it. Filtered out of the
  // menu for everybody else — a menu item that leads to a refusal is a worse
  // way of saying 'not for you' than not showing one.
  { group: 'Us', items: [{ path: '/console', icon: '⚙', label: 'The installation', needs: 'platform' }] },
  { group: 'Organisation', items: [
    { path: '/people', icon: '☰', label: 'Organisation info' },
    // Only for the people answerable for the organisation — the same rule the
    // API applies. See navFor: a menu item leading to a refusal is a worse way
    // of saying "not for you" than not showing one.
    { path: '/audit', icon: '⎘', label: 'Audit trail', needs: 'audit' },
    { path: '/safety', icon: '◎', label: 'Safety & compliance' },
    { path: '/training', icon: '◫', label: 'Training & authorisations' },
    { path: '/billing', icon: '€', label: 'Billing & invoices' },
    // Last, and after the working screens on purpose: it is about the
    // software rather than about an aircraft, and nobody navigates here as a
    // first move. It is under Organisation rather than Airworthiness for the
    // same reason.
    { path: '/manual', icon: '▤', label: 'Manual' },
  ] },
];

/**
 * The navigation this session actually gets.
 *
 * An organisation that manages airworthiness without holding parts has no
 * store, and a menu item that leads to a refusal is a worse way of saying so
 * than not showing one.
 */
function navFor(session: SignInResponse | null) {
  const enabled = session?.organisation?.storesEnabled ?? true;
  const platform = Boolean(session?.user?.platformAdmin);
  const readsAudit = canReadAudit();

  const allowed = (needs: string | undefined) =>
    needs === 'stores' ? enabled : needs === 'platform' ? platform : needs === 'audit' ? readsAudit : true;

  return NAV.map((g) => ({
    ...g,
    items: g.items.filter((i) => allowed('needs' in i ? i.needs : undefined)),
  })).filter((g) => g.items.length);
}

/**
 * Dialogs a link may open, and the control that opens each.
 *
 * `/fleet/:aircraftId/components?open=fit-component` renders the tab and then
 * presses the button. It exists so the helper can answer "how do I fit a
 * component?" with the form rather than with a screen the form is somewhere on
 * — the last step being exactly the step somebody stuck enough to ask cannot
 * take.
 *
 * An explicit table rather than a rule over the DOM, because the screens do not
 * agree on how a trigger is marked: `data-act` on the aircraft, `data-dialog`
 * on the dashboard, `data-open` on the organisation, bare boolean attributes in
 * stores. Inventing one convention for links to guess at would mean renaming
 * every control or shipping links that silently do nothing.
 *
 * `tools/check-manual.mjs` reads this table, so a manual link naming an action
 * that has since been renamed fails the build rather than the click.
 */
export const OPENABLE: Record<string, { path: string; selector: string }> = {
  // ── one aircraft ──────────────────────────────────────────────────────────
  'edit-aircraft':    { path: '/fleet/:aircraftId',                  selector: '[data-act="edit-aircraft"]' },
  'end-management':   { path: '/fleet/:aircraftId',                  selector: '[data-act="end-management"]' },
  'records-transfer': { path: '/fleet/:aircraftId',                  selector: '[data-act="records-transfer"]' },
  'add-task':         { path: '/fleet/:aircraftId/programme',        selector: '[data-act="add-task"]' },
  'revise-amp':       { path: '/fleet/:aircraftId/programme',        selector: '[data-act="revise-amp"]' },
  'amp-from-manuals': { path: '/fleet/:aircraftId/programme',        selector: '[data-act="amp-from-manuals"]' },
  'review-amp':       { path: '/fleet/:aircraftId/programme',        selector: '[data-act="review-amp"]' },
  'record-weighing':  { path: '/fleet/:aircraftId/weight-balance',   selector: '[data-act="record-weighing"]' },
  'define-stations':  { path: '/fleet/:aircraftId/weight-balance',   selector: '[data-act="define-stations"]' },
  'add-directive':    { path: '/fleet/:aircraftId/directives',       selector: '[data-act="add-directive"]' },
  'check-directives': { path: '/fleet/:aircraftId/directives',       selector: '[data-act="check-directives"]' },
  'ad-compliance':    { path: '/fleet/:aircraftId/directives',       selector: '[data-act="ad-compliance"]' },
  'fit-component':    { path: '/fleet/:aircraftId/components',       selector: '[data-act="fit-component"]' },
  'raise-defect':     { path: '/fleet/:aircraftId/defects',          selector: '[data-act="raise-defect"]' },
  'record-flight':    { path: '/fleet/:aircraftId/flights',          selector: '[data-act="record-flight"]' },

  // ── the fleet, and planning ───────────────────────────────────────────────
  'add-aircraft':     { path: '/fleet',                              selector: '[data-dialog="add-aircraft"]' },
  'new-work-pack':    { path: '/planning',                           selector: '[data-dialog="work-pack"]' },

  // ── stores ────────────────────────────────────────────────────────────────
  'receive-stock':    { path: '/stores',                             selector: '[data-receive]' },
  'part-off':         { path: '/stores',                             selector: '[data-reserve]' },
  'stock-check':      { path: '/stores',                             selector: '[data-check]' },
  'add-location':     { path: '/stores',                             selector: '[data-add-location]' },

  // ── the organisation ──────────────────────────────────────────────────────
  'edit-organisation':{ path: '/people',                             selector: '[data-open="edit-organisation"]' },
  'add-person':       { path: '/people',                             selector: '[data-open="add-person"]' },
  'add-approval':     { path: '/people',                             selector: '[data-open="add-approval"]' },
  'add-customer':     { path: '/people',                             selector: '[data-open="add-customer"]' },
  'edit-rates':       { path: '/people',                             selector: '[data-open="edit-rates"]' },
};

/** Whether a route pattern — the only placeholder being `:aircraftId` — fits this path. */
function matchesRoute(pattern: string, path: string): boolean {
  const a = pattern.split('/');
  const b = path.split('/');
  return a.length === b.length && a.every((seg, i) => seg.startsWith(':') || seg === b[i]);
}

/**
 * Press a control once the screen that owns it has drawn.
 *
 * Every view fetches before it renders and the router does not await any of
 * them, so there is no moment to hook. Polling for the control is the honest
 * version of "when it appears": it gives up after a few seconds rather than
 * waiting for ever, and a link naming a control that is not on this screen —
 * the store is switched off, the role does not allow it, the aircraft is
 * archived — quietly does nothing rather than hanging.
 *
 * Safe against pressing a button that is not wired yet: every view sets its
 * markup and binds its handlers in the same synchronous block, so a poll
 * between tasks never catches one half-built.
 *
 * setTimeout rather than requestAnimationFrame. A frame callback does not run
 * while the tab is not compositing — backgrounded, or in a window nobody is
 * looking at — so the link worked when watched and did nothing when not, which
 * is the worst way for anything to fail.
 */
function pressWhenDrawn(view: HTMLElement, selector: string, within = 4000): void {
  const deadline = Date.now() + within;
  const tick = () => {
    // Gone somewhere else while this was waiting. A detached view still answers
    // querySelector, and its screen may finish rendering into it long after the
    // shell has been redrawn — so without this, a slow link fires a dialog over
    // whatever the person moved on to.
    if (!view.isConnected) return;
    const el = view.querySelector<HTMLElement>(selector);
    if (el) return void el.click();
    if (Date.now() < deadline) setTimeout(tick, 40);
  };
  setTimeout(tick, 0);
}

const MOBILE = [
  { path: '/dashboard', icon: '◈', label: 'Dashboard' },
  { path: '/fleet', icon: '✈', label: 'Fleet' },
  { path: '/techlog', icon: '✎', label: 'Log' },
  { path: '/planning', icon: '▤', label: 'Plan' },
  { path: '/safety', icon: '◎', label: 'Safety' },
];

// ── routing ─────────────────────────────────────────────────────────────────

export function navigate(path: string, replace = false): void {
  if (replace) history.replaceState({}, '', path);
  else history.pushState({}, '', path);
  void route();
}

document.addEventListener('click', (e) => {
  const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="/"]');
  if (link && !link.hasAttribute('data-external') && !link.target) {
    e.preventDefault();
    navigate(link.getAttribute('href')!);
  }

  const row = (e.target as HTMLElement).closest<HTMLElement>('[data-aircraft]');
  if (row) navigate(`/fleet/${row.dataset.aircraft}`);

  // Dialogs are opened from a single place rather than wired per view: the
  // same two buttons appear on the dashboard, the fleet list, planning and the
  // aircraft record, and they should behave identically everywhere.
  const dialog = (e.target as HTMLElement).closest<HTMLElement>('[data-dialog]');
  if (dialog) {
    const reload = () => void route();
    const open =
      dialog.dataset.dialog === 'add-aircraft'
        ? addAircraft(reload)
        : dialog.dataset.dialog === 'work-pack'
          ? newWorkPack(dialog.dataset.forAircraft || undefined, reload)
          : null;
    // Fetching the context can fail on a dead connection; say so rather than
    // leaving a button that looks broken.
    open?.catch((err: unknown) =>
      toast(err instanceof Error ? err.message : 'Could not open that.', 'error'),
    );
  }

  /**
   * The estimate for a pack, from wherever a pack is on screen.
   *
   * Dispatched here for the same reason as the dialogs above: the button
   * appears in the planning list and inside the pack itself, and it should do
   * the same thing in both places rather than being wired twice.
   */
  const estimate = (e.target as HTMLElement).closest<HTMLElement>('[data-estimate]');
  if (estimate?.dataset.estimate) {
    openEstimate(estimate.dataset.estimate, estimate.dataset.ref ?? 'this pack');
  }

  const report = (e.target as HTMLElement).closest<HTMLElement>('[data-report]');
  if (report) {
    if (report.dataset.report === 'capacity') navigate('/planning/capacity');
    if (report.dataset.report === 'fleet-status') {
      exportFleetStatus().catch((err: unknown) =>
        toast(err instanceof Error ? err.message : 'Could not build the report.', 'error'),
      );
    }
  }

  const todo = (e.target as HTMLElement).closest<HTMLElement>('[data-todo]');
  if (todo) toast(`${todo.dataset.todo} is not built yet.`);
});

window.addEventListener('popstate', () => void route());

/**
 * Ctrl/Cmd-K opens the helper, Escape closes it.
 *
 * Bound to the document rather than the shell so it survives every re-render,
 * and guarded against firing while somebody is typing a registration into a
 * form — the shortcut is worth less than the sentence it would interrupt.
 */
document.addEventListener('keydown', (e) => {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    const el = document.activeElement;
    const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
    if (typing && !el.closest('.dock')) return;
    e.preventDefault();
    void assistantAvailable().then((yes) => yes && toggleAssistant());
    return;
  }
  // Not when a dialog is open: Escape belongs to whatever is on top, and the
  // dialog is.
  if (e.key === 'Escape' && !document.querySelector('dialog[open]')) closeAssistant();
});

// ── shell ───────────────────────────────────────────────────────────────────

/**
 * What the avatar says on hover. It is an identity indicator, not a menu —
 * the mockup had it that way and sign out is its own button — so the tooltip
 * has to carry enough to be worth hovering: who you are signed in as, in which
 * organisation, and in what role.
 */
function personLabel(session: SignInResponse): string {
  const name = `${session.user.firstName} ${session.user.lastName}`.trim() || session.user.email;
  const org = session.organisation;
  const role = org?.role ? org.role.replace(/_/g, ' ').toLowerCase() : null;
  return [name, session.user.email, org?.name, role].filter(Boolean).join(' — ');
}

/**
 * Whether the rail is folded to its icons.
 *
 * Kept in localStorage, which the access token deliberately is not — a token
 * there is readable by anything that gets script into the page, whereas
 * "this person likes a narrow menu" is neither secret nor worth a round trip.
 * It has to outlive the shell: the shell is rebuilt on every navigation, so a
 * preference held only in memory would unfold the moment anybody clicked
 * anything.
 */
const RAIL_KEY = 'airsight.rail';

let railShut = read(RAIL_KEY) === 'shut';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Storage can be denied outright — a locked-down profile, a browser in
    // private mode. The menu opens; nothing else is affected.
    return null;
  }
}

function shell(session: SignInResponse, path: string): string {
  const initials =
    `${session.user.firstName[0] ?? ''}${session.user.lastName[0] ?? ''}`.toUpperCase() || 'AS';
  const org = session.organisation;

  const navGroup = (g: (typeof NAV)[number], id: string) => html`
    <div class="navgrp">
      <div class="lbl" style="padding:0 9px 6px">${esc(g.group)}</div>
      <nav class="nav" id="${id}">
        ${g.items
          .map(
            // The label is a span rather than a bare text node so the folded
            // rail can hide it and leave the icon. `title` is what carries the
            // name once it is hidden — a column of unexplained glyphs is not
            // navigation.
            (i) => html`<a href="${i.path}" class="${path === i.path ? 'on' : ''}" title="${esc(i.label)}">
              <span class="ic">${i.icon}</span><span class="lb">${esc(i.label)}</span>
            </a>`,
          )
          .join('')}
      </nav>
    </div>
  `;

  return html`
    <div class="app on ${railShut ? 'railshut' : ''}">
      <aside class="rail">
        <a class="brand" href="/dashboard" title="FlyPack CAMO — back to the dashboard">
          <!-- The FlyPack mark: four tiles whose gaps form an aircraft seen from above. -->
          <svg class="mark" width="26" height="26" viewBox="0 0 64 64" aria-hidden="true"><path d="M0 33V6A6 6 0 0 1 6 0H29V20.3Z"/><path d="M64 33V6A6 6 0 0 0 58 0H35V20.3Z"/><path d="M0 40L29 27.3V48.3L18 53V58L29 53.3V64H6A6 6 0 0 1 0 58Z"/><path d="M64 40L35 27.3V48.3L46 53V58L35 53.3V64H58A6 6 0 0 0 64 58Z"/></svg>
          <div class="bt">
            <b>FlyPack</b><span>CAMO Software</span>
          </div>
        </a>
        ${navFor(session).map((g, i) => navGroup(g, `nav${i}`)).join('')}
        <div class="railfoot">
          <div class="orgcard">
            <div class="lbl">Organisation</div>
            <p class="tiny muted" style="margin-top:5px;line-height:1.45">
              ${esc(org?.name ?? '—')}<br /><b class="mono">${esc(org?.approvalReference ?? '')}</b>
            </p>
          </div>
        </div>
      </aside>

      <header class="topbar">
        <button class="railtoggle" type="button" id="railtoggle"
                title="${railShut ? 'Open the menu' : 'Fold the menu'}"
                aria-label="${railShut ? 'Open the menu' : 'Fold the menu'}"
                aria-expanded="${String(!railShut)}">${railShut ? '»' : '«'}</button>
        <div class="search">
          <span aria-hidden="true">⌕</span>
          <input type="search" placeholder="Registration, part number, AD reference…" aria-label="Search" />
        </div>
        <div style="margin-left:auto;display:flex;align-items:center;gap:12px">
          <button class="askbtn" type="button" id="ask" hidden aria-expanded="false">
            💬 Ask FlyPack CAMO <span class="k">Ctrl K</span>
          </button>
          <button class="btn sm" type="button" id="signout">Sign out</button>
          <button class="avatar" type="button" id="profile"
                  title="${esc(personLabel(session))} — your account">${esc(initials)}</button>
        </div>
      </header>

      <main class="main">
        ${readOnlyBanner(org)}
        <div class="wrap" id="view"></div>
      </main>

      <nav class="mobilebar">
        ${MOBILE.map(
          (i) => html`<button data-nav="${i.path}" class="${path === i.path ? 'on' : ''}">
            <span class="ic">${i.icon}</span>${esc(i.label)}
          </button>`,
        ).join('')}
      </nav>
    </div>
  `;
}

/**
 * Says the organisation is read-only, before anybody starts typing into it.
 *
 * Above the view rather than inside it, so it is on every screen and cannot be
 * scrolled past on the way to a form. The alternative — letting somebody fill
 * in a flight and meet the refusal on the way out — teaches them the
 * application is broken rather than that the trial has ended.
 *
 * It says what still works, and means it: reading, printing and exporting are
 * untouched, which is how an operator meets an obligation that is theirs
 * whatever they have paid us.
 */
function readOnlyBanner(org: OrganisationChoice | null | undefined): string {
  if (!org?.readOnly) return '';
  return html`
    <div class="wrap" style="padding-bottom:0">
      <div class="callout c-caution" style="margin:0 0 14px">
        <span class="cg">!</span>
        <div>
          <b>${esc(org.name)} is read-only.</b>
          ${esc(org.standingMessage ?? 'Nothing further can be recorded in it.')}
          A full copy of them can be downloaded from Settings at any time.
        </div>
      </div>
    </div>
  `;
}

const placeholder = (title: string) => html`
  <div class="phead"><div><h1>${esc(title)}</h1><p>Not built yet.</p></div></div>
  <div class="card">
    <div class="card-b">
      <div class="callout c-info">
        <span class="cg">i</span>
        <div>
          This screen exists in the mockup and is next in the build queue. The dashboard and fleet
          list are already reading live data from SQL Server.
        </div>
      </div>
    </div>
  </div>
`;

// ── router ──────────────────────────────────────────────────────────────────

let booted = false;

async function route(): Promise<void> {
  const path = location.pathname;
  const params = new URLSearchParams(location.search);

  // Restore the session once on first load using the httpOnly refresh cookie.
  if (!booted) {
    booted = true;
    if (!PUBLIC_ROUTES.includes(path)) await api.restore();
  }

  const session = getSession();

  // For the presentation site's home page, which sends somebody who uses the
  // application straight on to it (site/build.mjs). A hint, not a credential:
  // the session itself is still the httpOnly refresh cookie.
  try {
    if (session) localStorage.setItem('airsight.signedIn', '1');
    else localStorage.removeItem('airsight.signedIn');
  } catch {
    // Denied storage: the home page simply shows the presentation.
  }

  // "Open the demo" on the presentation site. Signs straight into the demo
  // fleet and lands on its dashboard, rather than at a sign-in form with the
  // same button on it. Whatever session this browser had is replaced, which is
  // what the button says it does. Public, so nothing is restored first.
  if (path === '/demo') {
    app.innerHTML = loading('Opening the demo');
    try {
      await api.demo();
      navigate('/dashboard', true);
    } catch (err) {
      const why = err instanceof ApiError ? err.message : 'The demo is unavailable.';
      navigate(`/login?error=${encodeURIComponent(why)}`, true);
    }
    return;
  }

  // Google sends the browser here after setting the cookie.
  if (path === '/auth/google/done') {
    await api.restore();
    navigate(getSession() ? '/dashboard' : '/login?error=Google+sign-in+did+not+complete', true);
    return;
  }

  if (!session) {
    if (!PUBLIC_ROUTES.includes(path)) {
      navigate('/login', true);
      return;
    }
    renderPublic(path, params);
    return;
  }

  // No organisation at all is not the same as several to choose between: there
  // is nothing to pick, so sign-up has to be finished first. Falling through to
  // the shell here is what produced an app that looked signed in and 401'd on
  // every request.
  if (session.needsOrganisation) {
    app.innerHTML = createOrgView(session);
    bindCreateOrg(app, () => void route());
    return;
  }

  if (session.mustSelectOrganisation) {
    app.innerHTML = chooseOrgView(session);
    bindChooseOrg(app, () => navigate('/dashboard', true));
    return;
  }

  // The bare address is the presentation site's now (web.config serves it
  // home.html); the dashboard lives at /dashboard. / still reaches here in
  // development, and from anything that links the old address client-side.
  if (PUBLIC_ROUTES.includes(path) || path === '/') {
    navigate('/dashboard', true);
    return;
  }

  // An owner account gets its own shell, not the CAMO's with items removed.
  // The API refuses them every other endpoint anyway; showing a navigation
  // full of things that will be refused is a worse way to say the same thing.
  if (session.organisation?.customerId) {
    app.innerHTML = ownerShell(session);
    app.querySelector('#signout')?.addEventListener('click', async () => {
      await api.logout();
      navigate('/login', true);
    });
    void renderOwnerPortal(app.querySelector<HTMLElement>('#view')!);
    return;
  }

  app.innerHTML = shell(session, path);
  bindSearch(app, (to) => navigate(to));
  app.querySelector('#signout')?.addEventListener('click', async () => {
    await api.logout();
    navigate('/login', true);
  });
  // The avatar is the only thing on every screen that is about the person
  // rather than the fleet, so it is where their own account lives.
  app.querySelector('#profile')?.addEventListener('click', () => editProfile());

  // Folding the rail is a class and nothing else — no re-render. Redrawing the
  // shell here would take the view with it, and losing a half-filled form to a
  // menu preference is not a trade anybody would make.
  const railBtn = app.querySelector<HTMLButtonElement>('#railtoggle');
  railBtn?.addEventListener('click', () => {
    railShut = !railShut;
    app.querySelector('.app')?.classList.toggle('railshut', railShut);
    const label = railShut ? 'Open the menu' : 'Fold the menu';
    railBtn.textContent = railShut ? '»' : '«';
    railBtn.title = label;
    railBtn.setAttribute('aria-label', label);
    railBtn.setAttribute('aria-expanded', String(!railShut));
    try {
      localStorage.setItem(RAIL_KEY, railShut ? 'shut' : 'open');
    } catch {
      // Denied storage: it still folds, it just will not be folded tomorrow.
    }
  });

  // Shown only where there is a helper to open. An installation without a key
  // gets no control at all rather than one that explains itself when clicked.
  const ask = app.querySelector<HTMLButtonElement>('#ask');
  ask?.addEventListener('click', () => toggleAssistant());
  void assistantAvailable().then((yes) => {
    if (yes && ask) ask.hidden = false;
  });
  app.querySelectorAll<HTMLButtonElement>('[data-nav]').forEach((b) =>
    b.addEventListener('click', () => navigate(b.dataset.nav!)),
  );

  const view = app.querySelector<HTMLElement>('#view')!;
  setContext(aircraftContext(path));

  /**
   * Arriving from an answer's "Take me there", at a dialog rather than a screen.
   *
   * `?open=fit-component` renders the screen and then presses the button, so
   * somebody who asked "how do I fit a component?" arrives at the form rather
   * than at a page with the form somewhere on it. That last step is the whole
   * difference: sending them to the screen and leaving them to find the control
   * is how the question got asked.
   *
   * The query is stripped first with replaceState rather than navigate — the
   * query has done its job and should not survive a refresh, but re-routing
   * here would redraw the shell under the `view` this function is holding.
   */
  const wanted = params.get('open');
  const openable = wanted ? OPENABLE[wanted] : undefined;
  if (openable && matchesRoute(openable.path, path)) {
    history.replaceState({}, '', path);
    pressWhenDrawn(view, openable.selector);
    // and falls through to the ordinary routing below, which draws it
  }

  if (path === '/dashboard') return void renderDashboard(view);
  if (path === '/fleet') return void renderFleet(view);
  if (path === '/planning') return void renderPlanning(view);
  // Before the work-pack route, which would otherwise read "capacity" as an id.
  if (path === '/planning/capacity') return void renderCapacity(view);
  if (path.startsWith('/planning/')) return void renderWorkPack(view, path.slice('/planning/'.length));
  if (path === '/techlog') return void renderTechlog(view);
  if (path === '/stores') return void renderStores(view);
  // Before the QR route below, which would read 'import' as a lot id.
  if (path === '/stores/import') return void renderStockImport(view);
  if (path.startsWith('/stores/checks/')) {
    return void renderStockCheck(view, path.slice('/stores/checks/'.length));
  }
  // What a label QR resolves to. Scanning a tin in the store opens its
  // record on whatever phone the person already has, signed in as they
  // already are.
  if (path.startsWith('/stores/')) {
    return void renderStores(view, path.slice('/stores/'.length));
  }
  if (path === '/console') return void renderConsole(view);
  if (path.startsWith('/console/')) return void renderConsole(view, path.slice('/console/'.length));
  if (path === '/people') return void renderOrganisation(view);
  if (path === '/audit') return void renderAudit(view);
  if (path === '/types') return void renderTypes(view);
  if (path === '/manual') return void renderManual(view);
  if (path.startsWith('/manual/')) return void renderManual(view, path.slice('/manual/'.length));
  if (path === '/defects') return void renderDefects(view);
  if (path === '/reliability') return void renderReliability(view);
  if (path === '/safety') return void renderSafety(view);
  if (path === '/training') return void renderTraining(view);
  if (path === '/billing') return void renderBilling(view);

  // Reading a programme out of the manuals. Before the /fleet/:id match,
  // which would otherwise read "amp" as a tab and send it back to overview.
  if (/^\/fleet\/[^/]+\/amp$/.test(path)) {
    return void renderAmpExtraction(view, path.split('/')[2]);
  }

  // /fleet/:id and /fleet/:id/:tab — the tab lives in the URL so a particular
  // view of an aircraft can be linked to and survives a refresh.
  if (path.startsWith('/fleet/')) {
    const [, , id, rawTab] = path.split('/');
    const known = TABS.some(([key]) => key === rawTab);
    if (rawTab && !known) {
      navigate(`/fleet/${id}`, true);
      return;
    }
    return void renderAircraft(view, id, (rawTab as TabKey) ?? 'overview');
  }

  const known = NAV.flatMap((g) => g.items).find((i) => i.path === path);
  view.innerHTML = placeholder(known?.label ?? 'Not found');
}

/**
 * Which record the helper should treat as "it".
 *
 * Only the aircraft: it is the one thing in this application that a whole
 * conversation hangs off, and the only one where "what is due on it" has an
 * unambiguous answer. Everything else the helper asks about by name.
 */
function aircraftContext(path: string): { aircraftId?: string; registration?: string; where?: string; path: string } {
  if (!path.startsWith('/fleet/')) return { path };
  const [, , id, tab] = path.split('/');
  if (!id) return { path };
  const label = TABS.find(([key]) => key === tab)?.[1].replace(/&amp;/g, '&');
  return {
    aircraftId: id,
    // Filled in by the aircraft view once it knows; the id alone would make a
    // chip nobody can read.
    registration: lastRegistration.id === id ? lastRegistration.registration : undefined,
    where: label ?? 'Overview',
    path,
  };
}

/** Remembered so the context chip can say YR-ZEN rather than a cuid. */
export const lastRegistration: { id: string | null; registration?: string } = { id: null };

/** Called by the aircraft view when it has the registration. */
export function noteRegistration(id: string, registration: string): void {
  lastRegistration.id = id;
  lastRegistration.registration = registration;
  setContext(aircraftContext(location.pathname));
}

function renderPublic(path: string, params: URLSearchParams): void {
  switch (path) {
    case '/register':
      app.innerHTML = registerView();
      bindRegister(app.querySelector('.auth-box')!);
      break;
    case '/forgot-password':
      app.innerHTML = forgotView();
      bindForgot(app.querySelector('.auth-box')!);
      break;
    case '/reset-password':
      app.innerHTML = resetView(params);
      bindReset(app.querySelector('.auth-box')!);
      break;
    case '/verify-email':
      app.innerHTML = verifyView();
      void bindVerify(app.querySelector('.auth-box')!, params);
      break;
    default:
      app.innerHTML = loginView(params);
      bindLogin(app.querySelector('.auth-box')!, (s) =>
        navigate(s.mustSelectOrganisation ? '/login' : '/dashboard', true),
      );
  }
}

void route();

/**
 * The owner's shell.
 *
 * No navigation, because there is nowhere else to go: the API refuses an owner
 * account every endpoint outside the portal, and a sidebar full of items that
 * would be refused is a worse way to say that than not showing one.
 */
function ownerShell(session: SignInResponse): string {
  const initials =
    `${session.user.firstName[0] ?? ''}${session.user.lastName[0] ?? ''}`.toUpperCase() || 'AS';
  const org = session.organisation;

  return html`
    <div class="app on" style="grid-template-columns:1fr">
      <header class="topbar" style="grid-column:1">
        <div style="display:flex;align-items:center;gap:10px">
          <!-- The FlyPack mark: four tiles whose gaps form an aircraft seen from above. -->
          <svg class="mark" width="26" height="26" viewBox="0 0 64 64" aria-hidden="true"><path d="M0 33V6A6 6 0 0 1 6 0H29V20.3Z"/><path d="M64 33V6A6 6 0 0 0 58 0H35V20.3Z"/><path d="M0 40L29 27.3V48.3L18 53V58L29 53.3V64H6A6 6 0 0 1 0 58Z"/><path d="M64 40L35 27.3V48.3L46 53V58L35 53.3V64H58A6 6 0 0 0 64 58Z"/></svg>
          <div style="line-height:1.2">
            <b>FlyPack CAMO</b>
            <span class="tiny dim" style="display:block">Owner portal</span>
          </div>
        </div>
        <div style="margin-left:auto;display:flex;align-items:center;gap:12px">
          <span class="tiny dim">Managed by ${esc(org?.name ?? 'your organisation')}</span>
          <button class="btn sm" type="button" id="signout">Sign out</button>
          <div class="avatar" title="${esc(session.user.email)}">${esc(initials)}</div>
        </div>
      </header>

      <main class="main" style="grid-column:1">
        ${readOnlyBanner(org)}
        <div class="wrap" id="view"></div>
      </main>
    </div>
  `;
}
